import useToasts from "./useToasts.ts";
import { useCallback, useId, useMemo } from "react";
import { DisplayableToast } from "./types.ts";

type ShowFn = (toast: DisplayableToast) => void;
type CloseFn = () => void;

/**
 * Manage a single toast. Repeated shows update the toast content, not
 * create a new toast
 * @see useToasts
 */
export default function useSingleToast({
  customId,
  targetId,
}: {
  customId?: string;
  targetId?: string;
} = {}): [ShowFn, CloseFn] {
  const { showToast, closeToast } = useToasts(targetId);
  const generatedId = useId();
  const id = customId ?? generatedId;

  const show: ShowFn = useCallback(
    (toast: DisplayableToast) => {
      showToast(toast, { id });
    },
    [showToast, id]
  );

  const close: CloseFn = useCallback(() => {
    closeToast(id);
  }, [closeToast, id]);

  return useMemo(() => [show, close], [show, close]);
}
